import { Dispatch } from '@reduxjs/toolkit';
import { Events } from 'src/constants/webSocketConsts/events';
import { ApiTags } from 'src/services/api';
import {
  BuilderMessage,
  builderSessionApi,
  ListBuilderMessagesResponse,
  mergeBuilderMessages,
} from 'src/services/api/builderSessionApi';
import { RootState } from 'src/store/reduxTypes';

const isBuilderMessage = (item: unknown): item is BuilderMessage =>
  typeof item === 'object' &&
  item !== null &&
  typeof (item as { id?: unknown }).id === 'string' &&
  typeof (item as { builderSessionId?: unknown }).builderSessionId === 'string';

/**
 * Handle incoming BUILDER_MESSAGE WS events. Returns a thunk so we can
 * access getState to find cached message-list queries via
 * selectInvalidatedBy, then optimistically merge the incoming messages
 * into each matching cache entry using the original query args.
 */
export const handleBuilderMessageUpdate = (event: Events, items: unknown[]) => {
  return (dispatch: Dispatch<any>, getState: () => RootState) => {
    if (event !== Events.Insert && event !== Events.Modify) {
      return;
    }

    const messages = items.filter(isBuilderMessage);
    if (messages.length === 0) {
      return;
    }

    // Group by session so we merge once per affected cache entry.
    const bySession = new Map<string, BuilderMessage[]>();
    for (const msg of messages) {
      const bucket = bySession.get(msg.builderSessionId);
      if (bucket) {
        bucket.push(msg);
      } else {
        bySession.set(msg.builderSessionId, [msg]);
      }
    }

    const state = getState();

    for (const [sessionId, sessionMessages] of bySession) {
      const affected = builderSessionApi.util.selectInvalidatedBy(state, [
        { type: ApiTags.builderMessages, id: `MESSAGES-${sessionId}` },
      ]);

      for (const { endpointName, originalArgs } of affected) {
        if (endpointName !== 'listBuilderSessionMessages') continue;

        dispatch(
          builderSessionApi.util.updateQueryData(
            'listBuilderSessionMessages',
            originalArgs as {
              appBuilderId: string;
              builderSessionId: string;
            },
            (draft: ListBuilderMessagesResponse) => {
              draft.data = mergeBuilderMessages(
                draft.data ?? [],
                sessionMessages,
              );
            },
          ),
        );
      }
    }
  };
};
